import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, RefreshCw, Search } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

// 系統日誌——全域錯誤／警告追蹤（秒速定位出錯地方）
// 數據源：system_logs 表（lib/syslog 寫入；workflow-cron 每日清理 30 日前舊記錄）

interface SyslogRow {
  id: number;
  level: string;
  source: string;
  message: string;
  detail: string | null;
  phone: string | null;
  order_num: string | null;
  created_at: number;
}

const AdminSyslog: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SyslogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdate, setLastUpdate] = useState('');

  const fetchLogs = async () => {
    const q = new URLSearchParams({ limit: '200' });
    if (level) q.set('level', level);
    if (search.trim()) q.set('search', search.trim());
    const res = await apiFetch(`/api/public/admin/syslog?${q.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setLoading(false); return; }
    setRows((data as any).data?.logs || []);
    setLastUpdate(new Date().toLocaleTimeString('zh-HK'));
    setLoading(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchLogs();
  }, [navigate, level]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(fetchLogs, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, level, search]);

  const levelCls = (lv: string) => lv === 'error'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : lv === 'warn' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      : 'bg-stone-100 text-stone-600 dark:bg-gray-700 dark:text-gray-300';
  const levelLabel = (lv: string) => lv === 'error' ? '❌ 錯誤' : lv === 'warn' ? '⚠️ 警告' : 'ℹ️ 資訊';

  const btn = 'px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700';
  const inputCls = 'px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white';

  return (
    <AdminLayout currentPage="syslog">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-red-600" /> 系統日誌
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">全域錯誤／警告追蹤——出錯秒速定位（保留 30 日，自動清理）</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputCls}>
            <option value="">全部級別</option>
            <option value="error">❌ 錯誤</option>
            <option value="warn">⚠️ 警告</option>
            <option value="info">ℹ️ 資訊</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜尋訊息／號碼／單號" className={inputCls + ' pl-7 w-56'} />
          </div>
          <button onClick={fetchLogs} className={btn + ' flex items-center gap-1'}><RefreshCw size={12} /> 重新整理</button>
          <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="w-3.5 h-3.5 accent-red-600" /> 每 30 秒自動刷新
          </label>
          {lastUpdate && <span className="text-xs text-gray-400">更新於 {lastUpdate}</span>}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中…</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 dark:bg-gray-700 text-left">
                <tr><th className="px-3 py-2">時間</th><th className="px-3 py-2">級別</th><th className="px-3 py-2">來源</th><th className="px-3 py-2">訊息</th><th className="px-3 py-2">關聯</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-stone-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{new Date(r.created_at * 1000).toLocaleString('zh-HK')}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${levelCls(r.level)}`}>{levelLabel(r.level)}</span></td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-300">{r.source}</td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{r.message}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{[r.phone, r.order_num].filter(Boolean).join(' / ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                無記錄——系統健康 ✓
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSyslog;
